import { type CSSProperties, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Country, CountryCode, Match, Variant, Venue } from '@/types/domain';
import { LiveChip, LiveProgressBlock } from '@/components/LiveState';
import { FlagStripe, FriendStack, VariantHeading } from '@/components/primitives';
import { LanguageSelector } from '@/components/LanguageSelector';
import { stageLabel } from '@/i18n/labels';
import { MockAd } from '@/components/MockAd';
import { formatKickoffDate, formatKickoffTime, formatTodayLong, formatTodayShort, isToday, isYesterday } from '@/utils/time';

/** Tournament-stage importance: higher = more prominent in sort. Used to
 * order multiple live matches in the hero carousel. The string-match is
 * lenient so seed-data variations (Final / 3rd place / etc.) don't break it.
 */
function stagePriority(stage: string): number {
  const s = stage.toLowerCase();
  if (s.includes('final') && !s.includes('semi') && !s.includes('quarter') && !s.includes('3rd'))
    return 6;
  if (s.includes('semi')) return 5;
  if (s.includes('3rd') || s.includes('third')) return 4;
  if (s.includes('quarter')) return 3;
  if (s.includes('round') || s.includes('r16')) return 2;
  if (s.includes('group')) return 1;
  return 0;
}

// "Bigness" ranking for ordering live games — biggest first. This is the knob
// we expect to evolve (FIFA ranking, per-user relevance, draw/betting interest);
// for now it's a hand-ranked marquee list of global + host-region draws. A match
// is as big as its more prominent side; codes not listed sort last.
const TEAM_PROMINENCE: string[] = [
  'BRA', 'ARG', 'FRA', 'GER', 'ESP', 'ENG', 'POR', 'NED',
  'MEX', 'USA', 'ITA', 'BEL', 'URU', 'CRO', 'COL', 'JPN', 'KOR', 'MAR',
];

function teamRank(code: string | null): number {
  if (!code) return Number.MAX_SAFE_INTEGER;
  const i = TEAM_PROMINENCE.indexOf(code);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

/** Lower = bigger. A match is as prominent as its more prominent team. */
function matchProminence(m: Match): number {
  return Math.min(teamRank(m.home), teamRank(m.away));
}

// Fixed-width box for 3-letter codes. Proportional letters ("IRQ" < "MEX")
// would otherwise push the adjacent flag to a different x on every row; a fixed
// width keeps the flags aligned in a clean column. Home code right-aligns
// (hugs its flag on the right); away code left-aligns (hugs its flag).
const CODE_BOX_BASE: CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: '#0a0a0a',
  display: 'inline-block',
  width: 34,
  flexShrink: 0,
};
const CODE_BOX_RIGHT: CSSProperties = { ...CODE_BOX_BASE, textAlign: 'right' };
const CODE_BOX_LEFT: CSSProperties = { ...CODE_BOX_BASE, textAlign: 'left' };

// Center-cell content for the shared SpineMatchRow. Upcoming → calm kickoff
// time; finished → the score (or a quiet "–" while it syncs).
function spineTimeCenter(m: Match): ReactNode {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: '#5a544c',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
      }}
    >
      {formatKickoffTime(m.kickoffAt)}
    </span>
  );
}

function spineScoreCenter(m: Match, t: TFunction): ReactNode {
  return m.score ? (
    <span
      style={{
        fontSize: 14,
        fontWeight: 800,
        color: '#0a0a0a',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: 0.5,
        whiteSpace: 'nowrap',
      }}
    >
      {m.score}
    </span>
  ) : (
    // Finished but no synced score — concise "Final" rather than a blank dash (SST-10).
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: '#9a9590',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        whiteSpace: 'nowrap',
      }}
    >
      {t('common:final')}
    </span>
  );
}

interface HomeScreenProps {
  variant: Variant;
  activeCode: CountryCode;
  countriesByCode: Partial<Record<CountryCode, Country>>;
  countryOrder: CountryCode[];
  matches: Match[];
  follows: Partial<Record<CountryCode, boolean>>;
  /** Per-country venue lists from the store — already prefetched at
   * boot for every followed country. Active country's first venue
   * drives the SmartSuggest card. */
  venuesByCountry: Partial<Record<CountryCode, Venue[]>>;
  /** Flat lookup so "Your teams" rows can resolve plan venueId →
   * venue without scanning every country. */
  venuesById: Record<string, Venue>;
  /** venueId → matchId of the active plan. */
  going: Record<string, string | undefined>;
  onPickCountry: (code: CountryCode) => void;
  onPickMatch: (match: Match) => void;
  /** Opens the map-anchored watch-party picker — used by "Your teams" rows,
   * where the intent is always to plan a spot for that match. */
  onPickWatchParty: (match: Match) => void;
  /** Open the schedule. Pass an array of country codes to filter the
   * schedule to matches involving any of those teams; omit for the full
   * tournament schedule. */
  onShowSchedule: (filterCodes?: CountryCode[]) => void;
  onShowFollow: () => void;
}

// Day in ms — used for the 7-day "Your teams" planning window.
const YOUR_TEAMS_LIMIT = 5;

/**
 * The big "look at this!" card for a currently-live match. Apple-style
 * restraint: pure white, no border, soft shadow, content does the work.
 *
 * Design choices:
 *  - Stage title stacks above stadium (two lines, not one tracked label)
 *  - LIVE indicator is just a red dot + red text (no pill, no shadow)
 *  - Score is the typographic hero — 64px, tabular, score numbers
 *    separated by whitespace alone (no dash)
 *  - Country full names below flags, not codes (more emotional)
 *  - Flags scaled up — they are the country identity
 */
function MatchHeroLive({
  match,
  home,
  away,
  onClick,
}: {
  match: Match;
  home: Country;
  away: Country;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  // v1 has no live score feed, so the hero never shows a scoreboard during
  // play — just the honest "Match in progress" state. The final score lands
  // at full time (SST-4).
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('home:liveAria', { home: home.name, away: away.name })}
      style={{
        background: '#fff',
        border: '1px solid #f0ede8',
        borderRadius: 14,
        padding: '14px 16px',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        display: 'block',
        marginBottom: 10,
      }}
    >
      {/* Top: stage/stadium title block (left) + LIVE indicator (right).
          Indicator baseline-aligns with the stage title so they read as
          peers, not parent/child. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 10,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#5a544c',
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {stageLabel(match.stage, t)} · {match.stadium}
          </div>
        </div>
        <LiveChip variant="bare" />
      </div>
      {/* Body: 3-column grid. Row 1 is flag · in-progress · flag (no
          scoreboard in v1 — there's no live score to show). Row 2 is
          name-blank-name beneath each flag's column. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          columnGap: 10,
          rowGap: 6,
        }}
      >
        {/* Row 1: flag · "Match in progress" · flag */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <FlagStripe code={home.code} colors={home.colors} w={36} h={26} radius={4} />
        </div>
        <LiveProgressBlock />
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <FlagStripe code={away.code} colors={away.colors} w={36} h={26} radius={4} />
        </div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: '#5a544c',
            lineHeight: 1.2,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {home.name}
        </span>
        <span aria-hidden="true" />
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: '#5a544c',
            lineHeight: 1.2,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {away.name}
        </span>
      </div>
    </button>
  );
}

/**
 * Vertical stack of live match heroes. All live matches visible at
 * once — no hidden-behind-a-swipe affordances. Order driven by the
 * caller (follows first, then stage importance).
 */
function LiveMatchStack({
  matches,
  countriesByCode,
  onPickMatch,
}: {
  matches: Match[];
  countriesByCode: Partial<Record<CountryCode, Country>>;
  onPickMatch: (m: Match) => void;
}) {
  if (matches.length === 0) return null;
  return (
    // Tight 8px gap within the live group so the cards read as a unit;
    // 16px marginBottom puts a clear break between the live group and
    // the regular kickoff list below.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
      {matches.map((m) => (
        <MatchHeroLive
          key={m.id}
          match={m}
          home={countriesByCode[m.home!]!}
          away={countriesByCode[m.away!]!}
          onClick={() => onPickMatch(m)}
        />
      ))}
    </div>
  );
}

export function HomeScreen({
  variant,
  activeCode,
  countriesByCode,
  countryOrder,
  matches,
  follows,
  venuesByCountry,
  venuesById,
  going,
  onPickCountry,
  onPickMatch,
  onPickWatchParty,
  onShowSchedule,
  onShowFollow,
}: HomeScreenProps) {
  const { t } = useTranslation();
  // Flat venueId → { venue, country } resolver. Backed by the store's
  // pre-built venuesById so we don't re-scan every country per render.
  // Returns undefined if the venue is in a country we haven't loaded
  // yet — callers fall back to a "Pick a spot" affordance.
  const resolveVenue = (venueId: string): { venue: Venue; country: Country } | undefined => {
    const venue = venuesById[venueId];
    if (!venue) return undefined;
    const country = countriesByCode[venue.countryCode];
    if (!country) return undefined;
    return { venue, country };
  };

  // Match buckets keyed off the follow graph rather than time-of-day.
  //
  //   yourTeams — followed-team matches surfaced as the actionable
  //               "Pick a watch party" rows. Includes today's
  //               followed matches (where the planning intent is
  //               highest) AND future-day followed matches. Excludes
  //               live (those have their own hero treatment) and past
  //               matches (no longer plannable). Sorted ascending by
  //               kickoff so today appears at the top.
  const { yourTeams, yourTeamsTotal } = useMemo(() => {
    const now = Date.now();
    const followsAny = (m: Match) =>
      (m.home && follows[m.home]) || (m.away && follows[m.away]);
    const ours: Match[] = [];
    for (const m of matches) {
      if (m.home === null || m.away === null) continue; // placeholder bracket
      if (m.live) continue;                              // live → hero band
      if (new Date(m.kickoffAt).getTime() < now) continue; // past, skip
      if (!followsAny(m)) continue;
      ours.push(m);
    }
    ours.sort(
      (a, b) =>
        new Date(a.kickoffAt).getTime() - new Date(b.kickoffAt).getTime(),
    );
    // Keep total separate from the truncated render list — the "See all"
    // CTA below shows only when there are more matches than we display.
    return {
      yourTeams: ours.slice(0, YOUR_TEAMS_LIMIT),
      yourTeamsTotal: ours.length,
    };
  }, [matches, follows]);

  // Live + today's kickoffs across all teams — drives the top
  // "Today's matches" band, which is score-focused (independent of
  // the planning-focused "Your teams · upcoming" band below).
  const todayMatches = useMemo(() => {
    const ms = matches.filter(
      (m) => m.home && m.away && (m.live || isToday(m.kickoffAt)),
    );
    ms.sort(
      (a, b) =>
        Number(b.live) - Number(a.live) ||
        new Date(a.kickoffAt).getTime() - new Date(b.kickoffAt).getTime(),
    );
    return ms;
  }, [matches]);

  // Today's split: live → hero band, everything else (followed or not)
  // → "Today's matches". Followed teams today aren't pinned in their
  // own slot anymore — they show up in the merged "Your teams" band
  // above this section with the actionable YourTeamsRow treatment.
  const { liveMatches, otherToday } = useMemo(() => {
    const live: Match[] = [];
    const other: Match[] = [];
    const followsAny = (m: Match) =>
      !!(m.home && follows[m.home]) || !!(m.away && follows[m.away]);
    for (const m of todayMatches) {
      // `status` is the backend poller's authoritative state, so LIVE is
      // trustworthy on its own — no minute-presence hack needed (v1 doesn't
      // populate a live minute, so the old `minute !== null` guard would have
      // hidden every real live match). Still require both countries resolved
      // so the hero band has flags to render.
      const isReallyLive = m.status === 'LIVE';
      if (
        isReallyLive &&
        m.home &&
        m.away &&
        countriesByCode[m.home] &&
        countriesByCode[m.away]
      ) {
        live.push(m);
      } else if (followsAny(m)) {
        // Followed-team match today → handled by YourTeamsSection
        // above. Skip here to avoid double-rendering.
        continue;
      } else {
        other.push(m);
      }
    }
    // Live sort: followed > stage > prominence (big games first) > minute.
    // Your followed team is always your biggest game; among the rest, marquee
    // teams lead so the hero's two slots show the biggest matches.
    live.sort((a, b) => {
      const aFollowed = followsAny(a);
      const bFollowed = followsAny(b);
      if (aFollowed !== bFollowed) return aFollowed ? -1 : 1;
      const stageDiff = stagePriority(b.stage) - stagePriority(a.stage);
      if (stageDiff !== 0) return stageDiff;
      const promDiff = matchProminence(a) - matchProminence(b); // lower = bigger
      if (promDiff !== 0) return promDiff;
      return (b.minute ?? 0) - (a.minute ?? 0);
    });
    return { liveMatches: live, otherToday: other };
  }, [todayMatches, follows, countriesByCode]);

  // Yesterday's results, surfaced below today so people checking the app
  // the morning after still see what happened. Finished matches only —
  // yesterday's "in progress" doesn't make sense, and unstarted-yesterday
  // rows would mean stale data. By status (not score), so a finished match
  // still syncing its score shows as FT rather than vanishing.
  const yesterdayMatches = useMemo(() => {
    const ms = matches.filter(
      (m) => m.home && m.away && m.status === 'FINISHED' && isYesterday(m.kickoffAt),
    );
    ms.sort(
      (a, b) =>
        new Date(a.kickoffAt).getTime() - new Date(b.kickoffAt).getTime(),
    );
    return ms;
  }, [matches]);
  // Collapsed by default — yesterday's results are reference, not the main
  // event. Single-row collapsed state keeps Home focused on today.
  // Yesterday collapse state — persisted across tab switches within a
  // single browser session. Casey (distracted mobile user) flips between
  // tabs constantly; resetting their expanded state every time would be
  // an annoying paper cut. Lazy initializer reads sessionStorage so the
  // first paint already reflects the persisted value (no flash).
  const YESTERDAY_OPEN_KEY = 'nynjwc:home-yesterday-open';
  const [yesterdayOpen, setYesterdayOpen] = useState<boolean>(() => {
    try {
      return window.sessionStorage.getItem(YESTERDAY_OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.sessionStorage.setItem(YESTERDAY_OPEN_KEY, yesterdayOpen ? '1' : '0');
    } catch {
      // Storage blocked → state still works in-memory for this session.
    }
  }, [yesterdayOpen]);

  // Scroll position persistence — same rationale as Yesterday. Save on
  // visibility change (tab leave) and on pagehide; restore on mount via
  // rAF so the page has laid out before we scroll. Clamps to the new
  // page bottom if the layout shrunk since the value was written.
  const SCROLL_KEY = 'nynjwc:home-scroll-y';
  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = window.sessionStorage.getItem(SCROLL_KEY);
    } catch {
      return;
    }
    if (raw === null) return;
    const target = Number.parseInt(raw, 10);
    if (!Number.isFinite(target) || target <= 0) return;
    const id = window.requestAnimationFrame(() => {
      const maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      window.scrollTo(0, Math.min(target, maxScroll));
    });
    return () => window.cancelAnimationFrame(id);
  }, []);
  useEffect(() => {
    function save() {
      try {
        window.sessionStorage.setItem(SCROLL_KEY, String(window.scrollY | 0));
      } catch {
        // Best-effort.
      }
    }
    function onVisibility() {
      if (document.hidden) save();
    }
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', save);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', save);
    };
  }, []);


  // Defensive guard: AppLoaded waits for store.ready before mounting,
  // so countriesByCode[activeCode] should always be defined. Keep the
  // bail just in case a stray code slips through. Placed after hooks
  // so render order stays stable.
  const country = countriesByCode[activeCode];
  if (!country) return null;
  const sug = country;
  const palette = sug.colors;

  const hasFollows = Object.values(follows).some(Boolean);
  const followedCount = Object.values(follows).filter(Boolean).length;
  const todayHeaderLong = formatTodayLong();
  const todayHeaderShort = formatTodayShort();
  const featured = venuesByCountry[activeCode]?.[0];

  function Header() {
    if (variant === 'editorial') {
      return (
        <div style={{ padding: 'max(28px, env(safe-area-inset-top)) 24px 16px', background: '#fbf8f3' }}>
          <div
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 11,
              color: '#8a7f72',
              textTransform: 'uppercase',
              letterSpacing: 1.4,
              marginBottom: 6,
            }}
          >
            {todayHeaderLong}
          </div>
          <div
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: 44,
              color: '#1a1612',
              letterSpacing: -1.2,
              lineHeight: 1,
              fontWeight: 400,
            }}
          >
            {t('home:todaysPossessive')}
            <br />
            <span style={{ fontStyle: 'italic' }}>{t('home:matchesLower')}</span>
          </div>
        </div>
      );
    }
    if (variant === 'minimal') {
      return (
        <div style={{ padding: 'max(28px, env(safe-area-inset-top)) 20px 12px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#9a9590',
              textTransform: 'uppercase',
              letterSpacing: 1.4,
              marginBottom: 4,
            }}
          >
            {todayHeaderShort}
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#0a0a0a', letterSpacing: -0.5 }}>{t('common:today')}</div>
        </div>
      );
    }
    return (
      <div
        style={{
          padding: 'max(24px, env(safe-area-inset-top)) 20px 14px',
          background: `linear-gradient(180deg, ${palette.primary}14, transparent)`,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#9a9590',
              textTransform: 'uppercase',
              letterSpacing: 1.4,
            }}
          >
            {t('home:matchday')}
          </div>
          <div
            style={{
              fontSize: 32,
              fontWeight: 900,
              color: '#0a0a0a',
              letterSpacing: -0.8,
              marginTop: 2,
            }}
          >
            {t('home:todayUpper')}
          </div>
        </div>
      </div>
    );
  }

  function CountryChips() {
    return (
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '0 20px 14px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        {countryOrder.map((c) => {
          const co = countriesByCode[c];
          if (!co || !co.curated) return null;
          if (!follows[c]) return null;
          // Home chips are pure nav — tapping drills into the Country
          // screen — so no chip should look "selected" here.
          return (
            <button
              key={c}
              type="button"
              onClick={() => onPickCountry(c)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                flexShrink: 0,
                background: '#fff',
                color: '#0a0a0a',
                border: '1px solid #ece8e2',
                borderRadius: 999,
                padding: '7px 12px 7px 8px',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <FlagStripe code={co.code} colors={co.colors} w={18} h={13} radius={2} />
              {co.code}
            </button>
          );
        })}
      </div>
    );
  }

  function SmartSuggest() {
    if (variant === 'editorial') {
      return (
        <div style={{ margin: '20px 24px 0', padding: '16px 0 18px', borderTop: '1px solid #e8e2d8' }}>
          <div
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 10,
              letterSpacing: 1.5,
              color: '#8a7f72',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {t('home:forYouDecorated')}
          </div>
          <div
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: 18,
              color: '#1a1612',
              lineHeight: 1.3,
            }}
          >
            {t('home:smartSuggestEditorialLead')}{' '}
            <span style={{ fontStyle: 'italic' }}>{featured?.name ?? t('home:aLocalSpot')}</span> {t('home:smartSuggestEditorialTail', { country: sug.name })}
          </div>
        </div>
      );
    }
    if (variant === 'minimal') {
      return (
        <div style={{ margin: '16px 20px 0', padding: '12px 0', borderTop: '1px solid #f0ede8' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: '#9a9590',
              textTransform: 'uppercase',
              letterSpacing: 1.2,
              marginBottom: 4,
            }}
          >
            {t('home:forYou')}
          </div>
          <div style={{ fontSize: 14, color: '#0a0a0a', lineHeight: 1.4 }}>
            {t('home:smartSuggestMinimalLead')} <b>{featured?.name ?? t('home:aLocalSpot')}</b> {t('home:smartSuggestMinimalTail', { country: sug.name })}
          </div>
        </div>
      );
    }
    return (
      <div
        style={{
          margin: '14px 20px 0',
          padding: '14px 16px',
          background: `linear-gradient(135deg, ${palette.primary}18, ${palette.secondary}10)`,
          border: '1px solid #ece8e2',
          borderRadius: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span
            style={{
              background: '#0a0a0a',
              color: '#fff',
              fontSize: 9,
              fontWeight: 800,
              padding: '3px 6px',
              borderRadius: 4,
              letterSpacing: 0.6,
            }}
          >
            {t('home:aiBadge')}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#5a544c',
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            {t('home:forYou')}
          </span>
        </div>
        <div style={{ fontSize: 14, color: '#0a0a0a', fontWeight: 500, lineHeight: 1.4 }}>
          {t('home:smartSuggestSportyLead')} <b>{featured?.name ?? t('home:aLocalSpot')}</b> {t('home:smartSuggestSportyTail', { country: sug.name })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <FriendStack count={5} size={22} />
          <span style={{ fontSize: 11, color: '#5a544c', fontWeight: 600 }}>{t('home:viaInstagram')}</span>
        </div>
      </div>
    );
  }

  // Each row in the "Your teams" band gets a status line: either where
  // you're going (green check + venue + neighborhood) or a "Pick a spot"
  // affordance. The whole row remains a single tap target — the pill is
  // a visual cue, not a separate button.
  function YourTeamsRow({ match }: { match: Match }) {
    if (!match.home || !match.away) return null;
    const home = countriesByCode[match.home];
    const away = countriesByCode[match.away];
    if (!home || !away) return null;

    let plannedVenue: { venue: Venue; country: Country } | undefined;
    for (const [venueId, mid] of Object.entries(going)) {
      if (mid === match.id) {
        plannedVenue = resolveVenue(venueId);
        break;
      }
    }

    const today = isToday(match.kickoffAt);

    return (
      <button
        type="button"
        onClick={() => onPickWatchParty(match)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          width: '100%',
          background: '#fff',
          border: '1px solid #f0ede8',
          borderRadius: 14,
          padding: '14px 16px',
          cursor: 'pointer',
          textAlign: 'left',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: today ? '#0a0a0a' : '#8a7f72',
            fontWeight: today ? 800 : 700,
            textTransform: 'uppercase',
            letterSpacing: 1.4,
          }}
        >
          {today ? t('common:today') : formatKickoffDate(match.kickoffAt)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <MatchBadge match={match} />
          <MatchTeamsLine match={match} home={home} away={away} align="start" />
        </div>

        {plannedVenue ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              background: `${plannedVenue.country.colors.primary}14`,
              borderRadius: 8,
              fontSize: 12,
              color: '#1a1612',
              fontWeight: 600,
            }}
          >
            <span
              style={{
                color: plannedVenue.country.colors.primary,
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              {t('common:check')}
            </span>
            <span>{t('home:goingTo', { venue: plannedVenue.venue.name })}</span>
            <span style={{ color: '#8a7f72', fontWeight: 500 }}>{t('home:venueHood', { hood: plannedVenue.venue.hood })}</span>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              background: '#f5f1ea',
              borderRadius: 8,
              fontSize: 12,
              color: '#5a544c',
              fontWeight: 600,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="#8a7f72" strokeWidth="1.6">
              <path d="M7 1.5c-2.2 0-4 1.8-4 4 0 2.7 4 7 4 7s4-4.3 4-7c0-2.2-1.8-4-4-4z" strokeLinejoin="round" />
              <circle cx="7" cy="5.5" r="1.4" />
            </svg>
            <span>{t('home:pickAWatchParty')}</span>
            <span style={{ marginLeft: 'auto', color: '#8a7f72', fontWeight: 700 }}>›</span>
          </div>
        )}
      </button>
    );
  }

  function YourTeamsSection() {
    if (!hasFollows) {
      return (
        <div style={{ padding: '16px 20px 0' }}>
          <div style={{ padding: '0 0 8px' }}>
            <VariantHeading variant={variant}>{t('home:yourTeams', { count: followedCount })}</VariantHeading>
          </div>
          <button
            type="button"
            onClick={onShowFollow}
            style={{
              display: 'block',
              width: '100%',
              background: `linear-gradient(135deg, ${palette.primary}18, ${palette.secondary}10)`,
              border: '1px solid #ece8e2',
              borderRadius: 14,
              padding: '20px 18px',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                fontSize: 10,
                letterSpacing: 1.5,
                color: '#5a544c',
                textTransform: 'uppercase',
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              {t('home:pickYourTeams')}
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0a0a0a', lineHeight: 1.4, marginBottom: 12 }}>
              {t('home:pickYourTeamsBody')}
            </div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#0a0a0a',
                color: '#fff',
                padding: '8px 14px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {t('home:pickFavorites')}
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="#fff" strokeWidth="2.2">
                <path d="M5 2l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </button>
        </div>
      );
    }

    return (
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{ padding: '0 0 8px' }}>
          <VariantHeading variant={variant}>{t('home:yourTeams', { count: followedCount })}</VariantHeading>
        </div>
        {/* Followed-team chips inline with the section — tapping a chip
            drills into that country's screen. Co-locates "the teams I
            follow" with "the matches those teams are playing." */}
        <div style={{ margin: '0 -20px 12px' }}>
          <CountryChips />
        </div>
        {yourTeams.length === 0 ? (
          <div
            style={{
              padding: '14px 16px',
              border: '1px dashed #ece8e2',
              borderRadius: 14,
              fontSize: 13,
              color: '#8a7f72',
              fontWeight: 500,
              marginBottom: 10,
            }}
          >
            {t('home:noUpcomingFollowed')}
          </div>
        ) : (
          <>
            {yourTeams.map((m) => (
              <YourTeamsRow key={m.id} match={m} />
            ))}
            {/* "See all" — appears only when YOUR_TEAMS_LIMIT truncated
                the list. Opens the schedule filtered to every team the
                user follows, so they can see the full slate (not just
                the next 5). */}
            {yourTeamsTotal > yourTeams.length ? (
              <button
                type="button"
                onClick={() => {
                  const followedCodes = (Object.keys(follows) as CountryCode[])
                    .filter((c) => follows[c]);
                  onShowSchedule(followedCodes);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  width: '100%',
                  background: 'transparent',
                  border: '1px dashed #ece8e2',
                  borderRadius: 12,
                  padding: '12px 14px',
                  marginTop: 2,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#5a544c',
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                <span>
                  {t('home:seeAllMatches', { count: yourTeamsTotal })}
                </span>
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="#5a544c"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M5 2l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : null}
          </>
        )}
      </div>
    );
  }

  // Shared visual vocabulary across all home-page row variants. Both
  // TodayMatchRow and YourTeamsRow use these so a reader pattern-matches
  // the same chrome across "today" and "future" sections.
  //
  // MatchBadge: three states keyed off `status`. Live → the shared "● LIVE"
  // ticker chip (no minute — v1 has no live clock); finished → muted brown FT;
  // upcoming → black kickoff time. Status is authoritative, so a finished match
  // reads as FT even before its score has synced.
  function MatchBadge({ match }: { match: Match }) {
    if (match.status === 'LIVE') {
      return <LiveChip />;
    }
    const finished = match.status === 'FINISHED';
    return (
      <div
        style={{
          background: finished ? '#8a7f72' : '#0a0a0a',
          color: '#fff',
          padding: '6px 10px',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: 0.5,
          flexShrink: 0,
          // Consistent width across states so the matchup starts at the same x.
          minWidth: 50,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        {finished ? t('common:ft') : formatKickoffTime(match.kickoffAt)}
      </div>
    );
  }

  // MatchTeamsLine: three balanced zones — home (left) · center cell · away
  // (right) — that fill the row width so the cell sits centered instead of the
  // whole thing clustering against the badge. Center cell states: a real score
  // (final), a live pulse dot (in progress — never a fake "vs", never a
  // scoreboard), a calm "–" for a finished score still syncing, or "vs"
  // upcoming.
  function MatchTeamsLine({
    match,
    home,
    away,
    align = 'center',
  }: {
    match: Match;
    home: Country;
    away: Country;
    align?: 'center' | 'start';
  }) {
    const centerCell =
      match.status === 'LIVE' ? (
        // Match the hero's language rather than a lone dot — the LIVE badge on
        // the left already carries the pulse.
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#5a544c',
            whiteSpace: 'nowrap',
          }}
        >
          {t('match:liveInProgress')}
        </span>
      ) : match.score ? (
        <span
          style={{
            fontSize: 15,
            fontWeight: 800,
            color: '#0a0a0a',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: 0.5,
          }}
        >
          {match.score}
        </span>
      ) : match.status === 'FINISHED' ? (
        <span
          aria-label={t('home:finalScoreSyncing')}
          style={{
            fontSize: 15,
            fontWeight: 800,
            color: '#c4beb6',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: 0.5,
          }}
        >
          –
        </span>
      ) : (
        <span style={{ fontSize: 11, color: '#9a9590', fontWeight: 600 }}>{t('common:vs')}</span>
      );

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: align === 'start' ? 'flex-start' : 'center',
          gap: 16,
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* home · center · away grouped — centered by default; `start` packs it
            left next to the time badge (compact Today rows). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={CODE_BOX_RIGHT}>{home.code}</span>
          <FlagStripe code={home.code} colors={home.colors} w={26} h={18} />
        </div>
        {centerCell}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FlagStripe code={away.code} colors={away.colors} w={26} h={18} />
          <span style={CODE_BOX_LEFT}>{away.code}</span>
        </div>
      </div>
    );
  }

  // Collapsed-by-default block at the very bottom of TodaysMatchesSection.
  // Tap the dashed pill to expand. Same chevron pattern as
  // ScheduleScreen's "Show earlier · N" so the visual vocabulary matches.
  function YesterdayBlock() {
    if (yesterdayMatches.length === 0) return null;
    return (
      // Wrapper provides the 16px top gap; this inner div is purely a
      // structural container for the pill + expanded list.
      <div>
        <button
          type="button"
          onClick={() => setYesterdayOpen((s) => !s)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            background: 'transparent',
            border: '1px dashed #ece8e2',
            borderRadius: 12,
            padding: '12px 14px',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            color: '#5a544c',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          <span>
            {yesterdayOpen
              ? t('home:hideYesterday', { count: yesterdayMatches.length })
              : t('home:showYesterday', { count: yesterdayMatches.length })}
          </span>
          <span style={{ flex: 1 }} />
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="#5a544c"
            strokeWidth="2"
            style={{
              transform: yesterdayOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 200ms',
            }}
          >
            <path d="M3 4.5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {yesterdayOpen ? (
          <div
            style={{
              marginTop: 8,
              background: '#fff',
              border: '1px solid #f0ede8',
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            {yesterdayMatches.map((m, i) => (
              <SpineMatchRow
                key={m.id}
                match={m}
                showDivider={i > 0}
                label="code"
                center={spineScoreCenter(m, t)}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  // Live now hero — full-bleed carousel of currently-live matches.
  // Renders only when something is live; otherwise the slot disappears
  // and Your-teams-today (or Today's matches) takes the top spot.
  //
  // Caps the hero treatment at 2 matches. Any additional live games fall
  // through to compact TodayMatchRow rendering below — they're still
  // visible and tappable, but the page doesn't drown in oversized cards
  // when the simulator (or real tournament day) has many concurrent
  // matches in progress.
  // Shared "spine" match row: label · flag · center · flag · label · chevron.
  // The flag·center·flag core is constant width (the center cell has a fixed
  // min-width), so flags line up across every row AND across sections. Used by
  // both "Also live" (center = bare ● LIVE, full names) and "Kicking off later"
  // (center = kickoff time, 3-letter codes) so they read as one family — no
  // heavy black time pill, no orphaned badge.
  function SpineMatchRow({
    match,
    showDivider,
    center,
    label,
  }: {
    match: Match;
    showDivider: boolean;
    center: ReactNode;
    label: 'name' | 'code';
  }) {
    if (!match.home || !match.away) return null;
    const home = countriesByCode[match.home];
    const away = countriesByCode[match.away];
    if (!home || !away) return null;
    const homeLabel = label === 'name' ? home.name : home.code;
    const awayLabel = label === 'name' ? away.name : away.code;
    const labelStyle: CSSProperties = {
      fontSize: 14,
      fontWeight: 700,
      color: '#0a0a0a',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
    };
    return (
      <button
        type="button"
        onClick={() => onPickMatch(match)}
        style={{
          // label · flag · center · flag · label · chevron. minmax(0, 1fr) on
          // the label columns lets long names truncate ("North Maced…").
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto auto auto minmax(0, 1fr) auto',
          alignItems: 'center',
          columnGap: 10,
          width: '100%',
          background: 'transparent',
          border: 'none',
          borderTop: showDivider ? '1px solid #f0ede8' : 'none',
          padding: '14px 16px',
          cursor: 'pointer',
        }}
      >
        <span style={{ ...labelStyle, textAlign: 'right' }}>{homeLabel}</span>
        <FlagStripe code={home.code} colors={home.colors} w={24} h={17} />
        {/* Fixed-width center keeps the flag·center·flag core identical across
            rows and sections, so flags align between Also live and Kicking off later. */}
        <div style={{ minWidth: 58, display: 'flex', justifyContent: 'center' }}>{center}</div>
        <FlagStripe code={away.code} colors={away.colors} w={24} h={17} />
        <span style={{ ...labelStyle, textAlign: 'left' }}>{awayLabel}</span>
        {/* Tap-through affordance — these rows open the match → watch-party picker. */}
        <svg
          width="7"
          height="12"
          viewBox="0 0 7 12"
          fill="none"
          stroke="#c4beb6"
          strokeWidth="2"
          aria-hidden="true"
          style={{ marginLeft: 6 }}
        >
          <path d="M1 1l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  }

  function LiveNowSection() {
    if (liveMatches.length === 0) return null;
    const HERO_LIMIT = 2;
    const heroLive = liveMatches.slice(0, HERO_LIMIT);
    const overflowLive = liveMatches.slice(HERO_LIMIT);
    return (
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{ padding: '0 0 8px' }}>
          <VariantHeading variant={variant}>{t('home:liveNow')}</VariantHeading>
        </div>
        <LiveMatchStack
          matches={heroLive}
          countriesByCode={countriesByCode}
          onPickMatch={onPickMatch}
        />
        {overflowLive.length > 0 && (
          <>
            <div
              style={{
                padding: '14px 0 8px',
                fontSize: 11,
                fontWeight: 700,
                color: '#8a7f72',
                textTransform: 'uppercase',
                letterSpacing: 1.2,
              }}
            >
              {t('home:alsoLive')}
            </div>
            <div
              style={{
                background: '#fff',
                border: '1px solid #f0ede8',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              {overflowLive.map((m, i) => (
                <SpineMatchRow
                  key={m.id}
                  match={m}
                  showDivider={i > 0}
                  label="name"
                  center={<LiveChip variant="bare" />}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  }


  // Everything else today (non-followed teams), split into Earlier today
  // (FT) and Kicking off later (upcoming). When the live + followed-today
  // bands above are both empty, this is the only today block, so its
  // upcoming header collapses to the legacy single-header treatment.
  //
  // "Earlier today" (done) vs "Kicking off later" (upcoming) is keyed off the
  // authoritative `status`, not a `score != null` proxy. A finished match
  // whose score is still syncing belongs under "Earlier today" as FT (with a
  // graceful pending placeholder) — the old proxy mislabeled it as upcoming.
  // Today's UPCOMING matches (non-followed teams). Surfaced high — right under
  // Live, above Your Teams — because "what's kicking off soon" is more
  // actionable than the longer-horizon Your-teams band or past results.
  function TodaysUpcomingSection() {
    const upcoming = otherToday.filter((m) => m.status !== 'FINISHED');
    if (upcoming.length === 0) return null;
    const ft = otherToday.filter((m) => m.status === 'FINISHED');
    // Generic "Today's matches" header only when this is effectively the lone
    // today block (nothing live, no followed teams, no earlier results).
    const showLegacySingleHeader =
      liveMatches.length === 0 && yourTeams.length === 0 && ft.length === 0;
    return (
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{ padding: '0 0 8px' }}>
          <VariantHeading variant={variant}>
            {showLegacySingleHeader ? t('home:todaysMatches') : t('home:kickingOffLater')}
          </VariantHeading>
        </div>
        {/* Same spine treatment as "Also live", with the kickoff time where the
            ● LIVE marker sits — flags align with the live list, no black pill. */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #f0ede8',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {upcoming.map((m, i) => (
            <SpineMatchRow
              key={m.id}
              match={m}
              showDivider={i > 0}
              label="code"
              center={spineTimeCenter(m)}
            />
          ))}
        </div>
      </div>
    );
  }

  // Today's FINISHED matches (non-followed teams) — results, shown lower down
  // since they're the least actionable. Same spine as the live/upcoming lists,
  // with the score in the center.
  function TodaysEarlierSection() {
    const ft = otherToday.filter((m) => m.status === 'FINISHED');
    if (ft.length === 0) return null;
    return (
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{ padding: '0 0 8px' }}>
          <VariantHeading variant={variant}>{t('home:earlierToday')}</VariantHeading>
        </div>
        <div
          style={{
            background: '#fff',
            border: '1px solid #f0ede8',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {ft.map((m, i) => (
            <SpineMatchRow
              key={m.id}
              match={m}
              showDivider={i > 0}
              label="code"
              center={spineScoreCenter(m, t)}
            />
          ))}
        </div>
      </div>
    );
  }

  // Empty state when nothing today at all — preserves the original
  // "Off day" copy. Yesterday still renders below this regardless.
  function TodaysEmptyState() {
    if (todayMatches.length > 0) return null;
    return (
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{ padding: '0 0 8px' }}>
          <VariantHeading variant={variant}>{t('home:todaysMatches')}</VariantHeading>
        </div>
        <NoMatchesToday />
      </div>
    );
  }

  function NoMatchesToday() {
    return (
      <div>
        <div
          style={{
            padding: '22px 18px',
            background: '#fff',
            border: '1px solid #f0ede8',
            borderRadius: 14,
          }}
        >
          <div
            style={{
              fontSize: 10,
              letterSpacing: 1.5,
              color: '#8a7f72',
              textTransform: 'uppercase',
              fontWeight: 700,
              marginBottom: 8,
            }}
          >
            {t('home:offDay')}
          </div>
          <div
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: '#0a0a0a',
              marginBottom: 6,
              letterSpacing: -0.3,
            }}
          >
            {t('home:quietOnPitch')}
          </div>
          <div style={{ fontSize: 13, color: '#5a544c', lineHeight: 1.5 }}>
            {t('home:noKickoffs')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <Header />
        {/* Language switcher floats in the header's top-right (SST-23). */}
        <div style={{ position: 'absolute', top: 'max(24px, env(safe-area-inset-top))', right: 16, zIndex: 5 }}>
          <LanguageSelector />
        </div>
      </div>

      {/* Personalized + temporal — the "today for me" core of the page. */}
      <LiveNowSection />

      {/* Today's upcoming matches — surfaced above Your Teams because
          "kicking off soon" is the most time-sensitive thing on the page. */}
      <TodaysUpcomingSection />

      {/* Your teams (merged today + future) — the high-intent, actionable
          band. Today's followed-team matches surface here at the top
          with the same "Pick a watch party" affordance as future matches,
          instead of being relegated to a non-actionable Today row. */}
      <YourTeamsSection />

      {/* Today's results (non-followed teams that already played) — least
          actionable, so it sits below Your Teams. */}
      <TodaysEarlierSection />
      <TodaysEmptyState />

      {/* Yesterday's results — collapsed by default. Renders only if
          there are yesterday matches; otherwise the pill is hidden. */}
      <div style={{ padding: '16px 20px 0' }}>
        <YesterdayBlock />
      </div>

      {/* Tail: management + navigation + ad. Demoted by /quieter — these
          used to be louder content slots higher up the page; they're
          now compact utility links that don't compete with match rows. */}

      {/* Followed-teams chip strip OR first-timer onboarding hint. When
          chip rail moved up into YourTeamsSection (chips belong next to
          the section that shows those teams' matches). The first-timer
          hint is also removed — YourTeamsSection's empty state already
          provides the same "Pick your teams" onboarding entry. */}

      {/* Schedule navigation → bordered pill. Quieter than the original
          full-width black bar, but more discoverable than the tiny text
          link the first polish pass left behind. Left-aligned so it
          shares the visual axis with section headings above. */}
      <div style={{ padding: '16px 20px 0' }}>
        <button
          type="button"
          onClick={() => onShowSchedule()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#fff',
            border: '1px solid #e0d8cb',
            borderRadius: 999,
            padding: '8px 14px 8px 12px',
            color: '#1a1612',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0.2,
            cursor: 'pointer',
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="#1a1612"
            strokeWidth="1.6"
            aria-hidden="true"
          >
            <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
            <path d="M5 1.5v3M11 1.5v3M2.5 6.5h11" strokeLinecap="round" />
          </svg>
          <span>{t('home:fullSchedule')}</span>
          <svg
            width="11"
            height="11"
            viewBox="0 0 14 14"
            fill="none"
            stroke="#5a544c"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M5 2l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <MockAd variant={variant} />

      {/* "✦ For you" AI suggestion — at the very bottom of the page,
          below the ad. Lowest-priority content; users who scroll all
          the way down opt into seeing it. */}
      <SmartSuggest />

      <div style={{ height: 100 }} />
    </>
  );
}
